import { z } from 'zod/v4';

export const FeatureFlagSourceV1Schema = z.enum(['global', 'org', 'default', 'expired', 'rollout']);
export type FeatureFlagSourceV1 = z.infer<typeof FeatureFlagSourceV1Schema>;

export const FeatureFlagVisibilityV1Schema = z.enum(['internal', 'admin']);
export type FeatureFlagVisibilityV1 = z.infer<typeof FeatureFlagVisibilityV1Schema>;

export const FeatureFlagRolloutV1Schema = z.strictObject({
  percentage: z.number().int().min(0).max(100),
  matched: z.boolean(),
});
export type FeatureFlagRolloutV1 = z.infer<typeof FeatureFlagRolloutV1Schema>;

export const FeatureFlagEvaluationV1Schema = z.strictObject({
  version: z.literal('v1'),
  key: z.string().min(1),
  enabled: z.boolean(),
  source: FeatureFlagSourceV1Schema,
  reason: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  rollout: FeatureFlagRolloutV1Schema,
  expiresAt: z.iso.datetime(),
});
export type FeatureFlagEvaluationV1 = z.infer<typeof FeatureFlagEvaluationV1Schema>;

export const FeatureFlagRecordV1Schema = z.strictObject({
  key: z.string().min(1),
  value: z.boolean(),
  orgId: z.uuid().nullable(),
  reason: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  rolloutPct: z.number().int().min(0).max(100),
  expiresAt: z.iso.datetime(),
  visibility: FeatureFlagVisibilityV1Schema,
  description: z.string(),
  owner: z.string().trim().min(1),
});
export type FeatureFlagRecordV1 = z.infer<typeof FeatureFlagRecordV1Schema>;

export const FeatureFlagUpsertRequestSchema = z.strictObject({
  key: z.string().min(1),
  value: z.boolean(),
  orgId: z.uuid().nullable().optional(),
  reason: z.string().trim().min(1).optional(),
  payload: z.record(z.string(), z.unknown()).default({}),
  rolloutPct: z.number().int().min(0).max(100).default(100),
  expiresAt: z.iso.datetime(),
  visibility: FeatureFlagVisibilityV1Schema.default('internal'),
  description: z.string().default(''),
  owner: z.string().trim().min(1).default('platform'),
});
export type FeatureFlagUpsertRequest = z.input<typeof FeatureFlagUpsertRequestSchema>;

export const FeatureFlagListResponseSchema = z.strictObject({
  flags: z.array(FeatureFlagRecordV1Schema),
});
export type FeatureFlagListResponse = z.infer<typeof FeatureFlagListResponseSchema>;

export const CostBudgetStatusSchema = z.strictObject({
  exceeded: z.boolean(),
  dailySpendUsd: z.number().nonnegative(),
  capUsd: z.number().nonnegative(),
  remainingUsd: z.number().nonnegative(),
});
export type CostBudgetStatus = z.infer<typeof CostBudgetStatusSchema>;

export const UsageCategorySummarySchema = z.strictObject({
  category: z.string().trim().min(1),
  totalUsd: z.number().nonnegative(),
  rowCount: z.number().int().nonnegative(),
});
export type UsageCategorySummary = z.infer<typeof UsageCategorySummarySchema>;

export const UsageLedgerRowSchema = z.strictObject({
  category: z.string().trim().min(1),
  vendor: z.string().trim().min(1).optional(),
  model: z.string().trim().min(1).optional(),
  tokensIn: z.number().int().nonnegative().optional(),
  tokensOut: z.number().int().nonnegative().optional(),
  tokensCached: z.number().int().nonnegative().optional(),
  costUsd: z.number().nonnegative(),
  timestamp: z.iso.datetime(),
});
export type UsageLedgerRow = z.infer<typeof UsageLedgerRowSchema>;

export const UsageSummaryResponseSchema = z.strictObject({
  budget: CostBudgetStatusSchema,
  last24hUsd: z.number().nonnegative(),
  last7dUsd: z.number().nonnegative(),
  last30dUsd: z.number().nonnegative(),
  categoryBreakdown: z.array(UsageCategorySummarySchema),
  recentRows: z.array(UsageLedgerRowSchema).default([]),
});
export type UsageSummaryResponse = z.infer<typeof UsageSummaryResponseSchema>;

export const AccountProfileV1Schema = z.strictObject({
  version: z.literal('v1'),
  userId: z.uuid(),
  email: z.email(),
  name: z.string().trim().min(1).nullable(),
  orgId: z.uuid(),
  orgName: z.string().trim().min(1),
  role: z.enum(['owner', 'admin', 'member']),
  timezone: z.string().trim().min(1).optional(),
});
export type AccountProfileV1 = z.infer<typeof AccountProfileV1Schema>;

export const InvoiceRecordV1Schema = z.strictObject({
  id: z.string().trim().min(1),
  date: z.iso.datetime(),
  amountUsd: z.number().nonnegative(),
  status: z.enum(['paid', 'open', 'void', 'past_due', 'uncollectible']),
  downloadUrl: z.url().optional(),
});
export type InvoiceRecordV1 = z.infer<typeof InvoiceRecordV1Schema>;

export const AccountBillingResponseSchema = z.discriminatedUnion('configured', [
  z.strictObject({
    version: z.literal('v1'),
    configured: z.literal(false),
    provider: z.null(),
    dailyBudgetUsd: z.number().nonnegative(),
    invoices: z.array(InvoiceRecordV1Schema),
    paymentMethodConfigured: z.literal(false),
    message: z.string().trim().min(1).optional(),
  }),
  z.strictObject({
    version: z.literal('v1'),
    configured: z.literal(true),
    provider: z.string().trim().min(1),
    planName: z.string().trim().min(1),
    dailyBudgetUsd: z.number().nonnegative(),
    invoices: z.array(InvoiceRecordV1Schema),
    paymentMethodConfigured: z.boolean(),
    message: z.string().trim().min(1).optional(),
  }),
]);
export type AccountBillingResponse = z.infer<typeof AccountBillingResponseSchema>;

export const AccountUsageResponseSchema = z.strictObject({
  version: z.literal('v1'),
  usage: UsageSummaryResponseSchema,
});
export type AccountUsageResponse = z.infer<typeof AccountUsageResponseSchema>;

export const AdminMetricSchema = z.strictObject({
  key: z.string().trim().min(1),
  value: z.number(),
  note: z.string().trim().min(1),
});
export type AdminMetric = z.infer<typeof AdminMetricSchema>;

export const AdminSummaryResponseSchema = z.strictObject({
  version: z.literal('v1'),
  configured: z.boolean(),
  metrics: z.array(AdminMetricSchema),
  topUsers: z.array(z.string().trim().min(1)),
  topTemplates: z.array(z.string().trim().min(1)),
  message: z.string().trim().min(1).optional(),
});
export type AdminSummaryResponse = z.infer<typeof AdminSummaryResponseSchema>;

export const PlatformUpdateItemSchema = z.strictObject({
  id: z.uuid(),
  publishedAt: z.iso.datetime(),
  title: z.string().trim().min(1),
  summary: z.string().trim().min(1),
  severity: z.enum(['info', 'warning', 'important']),
});
export type PlatformUpdateItem = z.infer<typeof PlatformUpdateItemSchema>;

export const PlatformUpdatesResponseSchema = z.strictObject({
  version: z.literal('v1'),
  configured: z.boolean(),
  updates: z.array(PlatformUpdateItemSchema),
  message: z.string().trim().min(1).optional(),
});
export type PlatformUpdatesResponse = z.infer<typeof PlatformUpdatesResponseSchema>;

export const BuyPlanSchema = z.strictObject({
  planId: z.string().trim().min(1),
  name: z.string().trim().min(1),
  interval: z.enum(['monthly', 'yearly']),
  priceUsd: z.number().nonnegative(),
  currency: z.string().trim().min(3).max(3),
  popular: z.boolean().default(false),
  ctaLabel: z.string().trim().min(1),
});
export type BuyPlan = z.infer<typeof BuyPlanSchema>;

export const PlatformBuyResponseSchema = z.strictObject({
  version: z.literal('v1'),
  configured: z.boolean(),
  plans: z.array(BuyPlanSchema),
  currentPlanId: z.string().trim().min(1).optional(),
  annualDiscountPercent: z.number().int().nonnegative().max(100).default(0),
  checkout: z.strictObject({
    enabled: z.boolean(),
    provider: z.string().trim().min(1).nullable(),
  }),
  message: z.string().trim().min(1).optional(),
});
export type PlatformBuyResponse = z.infer<typeof PlatformBuyResponseSchema>;

export const PreparedCapabilityStatusSchema = z.strictObject({
  enabled: z.boolean(),
  reason: z.enum(['enabled', 'disabled', 'not_configured', 'feature_flag_off']),
  message: z.string().trim().min(1).optional(),
});
export type PreparedCapabilityStatus = z.infer<typeof PreparedCapabilityStatusSchema>;

export const TargetPlatformStatusSchema = z.strictObject({
  id: z.string().trim().min(1),
  label: z.string().trim().min(1),
  status: z.enum(['enabled', 'disabled', 'not_configured']),
  reason: z.string().trim().min(1).optional(),
});
export type TargetPlatformStatus = z.infer<typeof TargetPlatformStatusSchema>;

export const PlatformCapabilitiesResponseSchema = z.strictObject({
  version: z.literal('v1'),
  proMode: PreparedCapabilityStatusSchema,
  devicePreview: PreparedCapabilityStatusSchema,
  targetPlatforms: z.array(TargetPlatformStatusSchema),
});
export type PlatformCapabilitiesResponse = z.infer<typeof PlatformCapabilitiesResponseSchema>;
